import { Autocomplete, TextField } from "@mui/material";
import { useEffect } from "react";

import { GqlPlannerV2AreaStatus } from "~/__generated__";
import useDebouncedState from "~/shared/hooks/useDebouncedState";

import usePlannerAreaSelect_GetAutocompleteOptions from "./api/usePlannerAreaSelect_GetAutocompleteOptions";

type PlannerAreaSelectProps = {
    label: string;
    value?: string[] | null;
    clientIds?: string[];
    projectIds?: string[];
    statuses?: GqlPlannerV2AreaStatus[];
    onChange: (value: string[]) => void;
};

export default function PlannerAreaSelect({ label, value, clientIds, projectIds, statuses, onChange }: PlannerAreaSelectProps) {
    const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState("");

    const { options, selectedOptions, scrubbedSelectedIds, selectedIdsIsScrubbed, loading } =
        usePlannerAreaSelect_GetAutocompleteOptions({
            clientIds: clientIds,
            projectIds: projectIds,
            statuses: statuses,
            q: debouncedSearchQuery,
            selectedIds: value,
        });

    // Drop area ids from the url whose client or project is no longer part of the active filters.
    useEffect(() => {
        if (!selectedIdsIsScrubbed) return;

        onChange(scrubbedSelectedIds);
    }, [onChange, scrubbedSelectedIds, selectedIdsIsScrubbed]);

    return (
        <Autocomplete
            multiple
            options={options}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            loading={loading}
            getOptionKey={option => option.value}
            getOptionLabel={option => option.label}
            value={selectedOptions}
            onChange={(_, newValue) => onChange(newValue.map(x => x.value))}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") return;
                setSearchQuery(newInputValue);
            }}
            renderInput={params => <TextField {...params} fullWidth variant="outlined" label={label} />}
        />
    );
}
